import React from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { ClipboardList, Copy, MessageSquareText, SendHorizonal, Settings2 } from 'lucide-react'
import { Toaster, toast } from 'sonner'

import { AdminTab } from './components/console/admin-tab'
import { MessagesTab } from './components/console/messages-tab'
import { PublishTab } from './components/console/publish-tab'
import { RequestsTab } from './components/console/requests-tab'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog'
import {
  ALL_SELECTION,
  AUTO_SELECTION,
  MESSAGE_TYPE_OPTIONS,
  NO_SELECTION,
  RANDOM_CODE_SELECTION,
  RANDOM_RECEIVER_ACCOUNT_SELECTION,
  REJECTION_CODE_INFOS,
  SCENARIO_INFOS,
} from './console-constants'
import type {
  DetailState,
  HealthState,
  InlineResult,
  MessageItem,
  MockConfig,
  OutboundResponseMode,
  PublishResult,
  ReceiverAccount,
  RequestItem,
} from './console-types'
import { api, getOrCreateSessionId, getSessionBaseUrl, prettyJson } from './lib/api'
import { cn } from './lib/utils'

declare global {
  interface Window {
    __icomMockUiRoot?: Root
  }
}

type PublishResponse = {
  messageid?: string
  endtoendid?: string
  cenario?: string
  cenarioEfetivo?: string
  total?: number
  items?: Array<{ messageid: string; endtoendid: string; cenarioEfetivo?: string }>
  delayEntreMensagensMs?: number
  message?: string
}

const HEALTH_CHECK_INTERVAL_MS = 30_000
const REQUESTS_ENDPOINT = '/_mock/requests'
const MESSAGES_ENDPOINT = '/_mock/messages'
const CONFIG_ENDPOINT = '/_mock/config'
const PUBLISH_ENDPOINT = '/_mock/lp/pacs/008'
const RECEIVER_ACCOUNTS_STORAGE_KEY = 'icom-mock.receiver-accounts-v2'

const DEFAULT_RECEIVER_ACCOUNTS: ReceiverAccount[] = [{ conta: '991199' }]
const DEFAULT_SELECTED_SCENARIOS = ['DICT']

const NAV_ITEMS = [
  { id: 'publish', label: 'Publicar', icon: SendHorizonal },
  { id: 'requests', label: 'Requests', icon: ClipboardList },
  { id: 'messages', label: 'Mensagens', icon: MessageSquareText },
  { id: 'admin', label: 'Admin', icon: Settings2 },
] as const

function App() {
  const [health, setHealth] = React.useState<HealthState>('checking')
  const [activeTab, setActiveTab] = React.useState('publish')
  const [sessionId] = React.useState(() => getOrCreateSessionId())
  const sessionBaseUrl = React.useMemo(() => getSessionBaseUrl(sessionId), [sessionId])
  const receiverAccountsStorageKey = `${RECEIVER_ACCOUNTS_STORAGE_KEY}.${sessionId}`

  // Scenario selection (checklist)
  const [selectedScenarios, setSelectedScenarios] = React.useState<string[]>(DEFAULT_SELECTED_SCENARIOS)

  // Publish form
  const [expectedResponse, setExpectedResponse] = React.useState(NO_SELECTION)
  const [expectedRejectCode, setExpectedRejectCode] = React.useState(RANDOM_CODE_SELECTION)
  const [payerAccountType, setPayerAccountType] = React.useState(AUTO_SELECTION)
  const [receiverAccountSelection, setReceiverAccountSelection] = React.useState('991199')
  const [receiverAccounts, setReceiverAccounts] = React.useState<ReceiverAccount[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_RECEIVER_ACCOUNTS
    try {
      const stored =
        window.localStorage.getItem(receiverAccountsStorageKey) ??
        window.localStorage.getItem(RECEIVER_ACCOUNTS_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as unknown
        if (Array.isArray(parsed) && parsed.length > 0) return parsed as ReceiverAccount[]
      }
    } catch {
      // ignore
    }
    return DEFAULT_RECEIVER_ACCOUNTS
  })
  const [transactionPurpose, setTransactionPurpose] = React.useState(AUTO_SELECTION)
  const [transactionPriority, setTransactionPriority] = React.useState(AUTO_SELECTION)
  const [isPublishing, setIsPublishing] = React.useState(false)
  const [publishResult, setPublishResult] = React.useState<PublishResult | null>(null)

  // Requests tab
  const [requests, setRequests] = React.useState<RequestItem[]>([])
  const [requestsTotal, setRequestsTotal] = React.useState(0)
  const [isLoadingRequests, setIsLoadingRequests] = React.useState(false)

  // Messages tab
  const [messages, setMessages] = React.useState<MessageItem[]>([])
  const [messageTypeFilter, setMessageTypeFilter] = React.useState(ALL_SELECTION)
  const [messageSearch, setMessageSearch] = React.useState('')
  const [isLoadingMessages, setIsLoadingMessages] = React.useState(false)

  // Admin tab
  const [configMode, setConfigMode] = React.useState<OutboundResponseMode>('ACCC')
  const [configRejectCode, setConfigRejectCode] = React.useState('AG03')
  const [isSavingConfig, setIsSavingConfig] = React.useState(false)
  const [isResetting, setIsResetting] = React.useState(false)
  const [configResult, setConfigResult] = React.useState<InlineResult | null>(null)
  const [resetResult, setResetResult] = React.useState<InlineResult | null>(null)

  // Detail modal
  const [detailState, setDetailState] = React.useState<DetailState>({
    open: false,
    title: '',
    description: '',
    data: null,
  })

  const checkHealth = React.useCallback(async () => {
    setHealth('checking')
    try {
      const { ok, data } = await api<{ ok?: boolean }>('GET', '/health')
      setHealth(ok && data?.ok ? 'ok' : 'error')
    } catch {
      setHealth('error')
    }
  }, [])

  const loadConfig = React.useCallback(async () => {
    try {
      const { ok, data } = await api<MockConfig>('GET', CONFIG_ENDPOINT)
      if (!ok || !data) return
      setConfigMode(data.outboundPacs008Response ?? 'ACCC')
      setConfigRejectCode(data.outboundPacs008RejectCode ?? 'AG03')
    } catch {
      // ignore
    }
  }, [])

  const loadRequests = React.useCallback(async () => {
    setIsLoadingRequests(true)
    try {
      const { data } = await api<{ total?: number; items?: RequestItem[] }>('GET', REQUESTS_ENDPOINT)
      setRequests(data?.items ?? [])
      setRequestsTotal(data?.total ?? 0)
    } catch {
      toast.error('Erro ao carregar requests')
    } finally {
      setIsLoadingRequests(false)
    }
  }, [])

  const loadMessages = React.useCallback(async () => {
    setIsLoadingMessages(true)
    try {
      const params = new URLSearchParams()
      if (messageTypeFilter !== ALL_SELECTION) params.set('type', messageTypeFilter)
      if (messageSearch.trim()) params.set('search', messageSearch.trim())
      params.set('limit', '100')
      const { data } = await api<{ items?: MessageItem[] }>('GET', `${MESSAGES_ENDPOINT}?${params.toString()}`)
      setMessages(data?.items ?? [])
    } catch {
      toast.error('Erro ao carregar mensagens')
    } finally {
      setIsLoadingMessages(false)
    }
  }, [messageSearch, messageTypeFilter])

  React.useEffect(() => {
    void loadConfig()
    void checkHealth()
    const interval = setInterval(() => void checkHealth(), HEALTH_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [checkHealth, loadConfig])

  React.useEffect(() => {
    if (activeTab === 'requests') void loadRequests()
  }, [activeTab, loadRequests])

  React.useEffect(() => {
    if (activeTab !== 'messages') return
    const timer = setTimeout(() => void loadMessages(), 250)
    return () => clearTimeout(timer)
  }, [activeTab, loadMessages])

  // Persist receiver accounts
  React.useEffect(() => {
    window.localStorage.setItem(receiverAccountsStorageKey, JSON.stringify(receiverAccounts))
  }, [receiverAccounts, receiverAccountsStorageKey])

  // Reset selection if selected account is removed
  React.useEffect(() => {
    if (
      receiverAccountSelection !== RANDOM_RECEIVER_ACCOUNT_SELECTION &&
      !receiverAccounts.some((a) => a.conta === receiverAccountSelection)
    ) {
      setReceiverAccountSelection(RANDOM_RECEIVER_ACCOUNT_SELECTION)
    }
  }, [receiverAccountSelection, receiverAccounts])

  async function handlePublishSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedScenarios.length) {
      toast.error('Selecione ao menos um cenário.')
      return
    }
    if (!receiverAccounts.length) {
      toast.error('Adicione pelo menos uma conta de destino.')
      return
    }

    const payload: Record<string, unknown> = {}

    // Build payload from form fields (pagador, transação, avançados)
    for (const [key, value] of new FormData(event.currentTarget).entries()) {
      const trimmed = String(value).trim()
      if (trimmed) payload[key] = trimmed
    }

    // Numeric coercions
    if (payload.quantidade) payload.quantidade = Number(payload.quantidade)
    if (payload.delayEntreMensagensMs) payload.delayEntreMensagensMs = Number(payload.delayEntreMensagensMs)

    // Cenário: pick randomly from selected list
    const pickedScenario =
      selectedScenarios.length === 1
        ? selectedScenarios[0]
        : selectedScenarios[Math.floor(Math.random() * selectedScenarios.length)]
    payload.cenario = pickedScenario

    // Receiver: from account list (not from FormData)
    delete payload.contaUsuarioRecebedor
    delete payload.nomeUsuarioRecebedor
    delete payload.cpfCnpjUsuarioRecebedor
    delete payload.agenciaUsuarioRecebedor
    delete payload.participanteDoUsuarioRecebedor
    delete payload.tipoContaUsuarioRecebedor

    if (receiverAccountSelection !== RANDOM_RECEIVER_ACCOUNT_SELECTION) {
      const acct = receiverAccounts.find((a) => a.conta === receiverAccountSelection)
      if (acct) {
        payload.contaUsuarioRecebedor = acct.conta
        if (acct.nome) payload.nomeUsuarioRecebedor = acct.nome
        if (acct.cpfCnpj) payload.cpfCnpjUsuarioRecebedor = acct.cpfCnpj
        if (acct.agencia) payload.agenciaUsuarioRecebedor = acct.agencia
        if (acct.ispb) payload.participanteDoUsuarioRecebedor = acct.ispb
        if (acct.tipoConta) payload.tipoContaUsuarioRecebedor = acct.tipoConta
      }
    } else {
      payload.contasUsuarioRecebedor = receiverAccounts.map((a) => a.conta)
    }

    setIsPublishing(true)
    try {
      const { ok, data } = await api<PublishResponse>('POST', PUBLISH_ENDPOINT, payload)

      if (!ok) {
        setPublishResult({ variant: 'error', title: 'Erro', body: prettyJson(data) })
        toast.error(data?.message || 'Erro ao publicar')
        return
      }

      if (data?.items?.length) {
        setPublishResult({
          variant: 'success',
          title: `${data.total} transação(ões) publicadas — ${data.cenario}${data.cenarioEfetivo ? ` → ${data.cenarioEfetivo}` : ''}${data.delayEntreMensagensMs ? ` · delay ${data.delayEntreMensagensMs}ms` : ''}`,
          body: data.items
            .map(
              (item, i) =>
                `#${i + 1}  [${item.cenarioEfetivo ?? data.cenario}]  messageId: ${item.messageid}  endToEndId: ${item.endtoendid}`
            )
            .join('\n'),
        })
      } else {
        setPublishResult({
          variant: 'success',
          title: `Transação publicada — ${data?.cenario}${data?.cenarioEfetivo ? ` → ${data.cenarioEfetivo}` : ''}`,
          body: `messageId:  ${data?.messageid}\nendToEndId: ${data?.endtoendid}`,
        })
      }

      toast.success(`${data?.total ?? 1} transação(ões) publicada(s) no Long Poll!`)
    } catch {
      toast.error('Erro de conexão com o mock')
    } finally {
      setIsPublishing(false)
    }
  }

  async function handleSaveConfig(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const body: MockConfig = { outboundPacs008Response: configMode }
    if (configMode === 'RJCT' && configRejectCode.trim()) {
      body.outboundPacs008RejectCode = configRejectCode.trim()
    }
    setIsSavingConfig(true)
    try {
      const { ok, data } = await api<MockConfig>('PUT', CONFIG_ENDPOINT, body)
      setConfigResult({ variant: ok ? 'success' : 'error', body: prettyJson(data) })
      if (ok) toast.success('Configuração salva')
      else toast.error('Falha ao salvar')
    } catch {
      toast.error('Erro de conexão')
    } finally {
      setIsSavingConfig(false)
    }
  }

  async function handleClearRequests() {
    if (!confirm('Limpar todas as requests e resetar o estado do mock?')) return
    try {
      const { ok } = await api('DELETE', REQUESTS_ENDPOINT)
      if (!ok) { toast.error('Falha ao limpar requests'); return }
      toast.success('Estado resetado')
      void loadRequests()
    } catch {
      toast.error('Erro ao limpar')
    }
  }

  async function handleResetRuntime() {
    setIsResetting(true)
    try {
      const { ok } = await api('DELETE', REQUESTS_ENDPOINT)
      setResetResult({
        variant: ok ? 'success' : 'error',
        body: ok ? 'Estado resetado com sucesso.' : 'Falha ao resetar.',
      })
      if (!ok) { toast.error('Falha no reset'); return }
      toast.success('Reset completo')
      if (activeTab === 'requests') void loadRequests()
    } catch {
      toast.error('Erro de conexão')
    } finally {
      setIsResetting(false)
    }
  }

  function openDetail(title: string, data: unknown, description?: string) {
    setDetailState({ open: true, title, description, data })
  }

  async function copySessionBaseUrl() {
    try {
      await navigator.clipboard.writeText(sessionBaseUrl)
      toast.success('Base URL copiada')
    } catch {
      toast.error('Não foi possível copiar a Base URL')
    }
  }

  return (
    <Dialog
      open={detailState.open}
      onOpenChange={(open) => setDetailState((current) => ({ ...current, open }))}
    >
      <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
        {/* Header */}
        <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-card px-5">
          <div className="flex items-center gap-3">
            <div className="flex size-7 items-center justify-center rounded bg-primary/15">
              <span className="font-display text-[10px] font-bold tracking-widest text-primary">IC</span>
            </div>
            <span className="font-display text-sm font-bold tracking-wide">ICOM Mock</span>
            <span className="font-mono text-xs text-muted-foreground/60">· console</span>
          </div>
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex min-w-0 items-center gap-2 rounded border border-border/60 bg-background/70 px-3 py-1">
              <span className="shrink-0 font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Base URL operador
              </span>
              <code className="truncate font-mono text-xs text-foreground/80">{sessionBaseUrl}</code>
              <button
                type="button"
                onClick={() => void copySessionBaseUrl()}
                className="shrink-0 text-primary hover:text-primary/80"
                title="Copiar Base URL"
              >
                <Copy className="size-3.5" />
              </button>
            </div>
            <div
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 font-mono text-xs',
                health === 'ok' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
                health === 'error' && 'border-red-500/30 bg-red-500/10 text-red-400',
                health === 'checking' && 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400'
              )}
            >
              <span
                className={cn(
                  'size-1.5 rounded-full',
                  health === 'ok' && 'bg-emerald-400',
                  health === 'error' && 'bg-red-400',
                  health === 'checking' && 'animate-pulse bg-yellow-400'
                )}
              />
              {health === 'checking' ? 'conectando' : health === 'ok' ? 'online' : 'offline'}
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <nav className="flex w-52 shrink-0 flex-col border-r border-border bg-card py-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    'flex items-center gap-3 border-l-2 px-4 py-2.5 text-sm font-medium transition-colors',
                    activeTab === item.id
                      ? 'border-primary bg-secondary text-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-secondary/40 hover:text-foreground'
                  )}
                >
                  <Icon className="size-4 shrink-0" />
                  {item.label}
                </button>
              )
            })}
            <div className="mt-auto border-t border-border px-4 py-3">
              <p className="font-mono text-[10px] text-muted-foreground/40">mock · spi · pix</p>
            </div>
          </nav>

          {/* Main content */}
          <main className="flex-1 overflow-auto p-6">
            {activeTab === 'publish' && (
              <PublishTab
                scenarios={SCENARIO_INFOS}
                selectedScenarios={selectedScenarios}
                setSelectedScenarios={setSelectedScenarios}
                expectedResponse={expectedResponse}
                setExpectedResponse={setExpectedResponse}
                expectedRejectCode={expectedRejectCode}
                setExpectedRejectCode={setExpectedRejectCode}
                rejectionCodes={REJECTION_CODE_INFOS}
                payerAccountType={payerAccountType}
                setPayerAccountType={setPayerAccountType}
                receiverAccountSelection={receiverAccountSelection}
                setReceiverAccountSelection={setReceiverAccountSelection}
                receiverAccounts={receiverAccounts}
                setReceiverAccounts={setReceiverAccounts}
                transactionPurpose={transactionPurpose}
                setTransactionPurpose={setTransactionPurpose}
                transactionPriority={transactionPriority}
                setTransactionPriority={setTransactionPriority}
                isPublishing={isPublishing}
                publishResult={publishResult}
                onSubmit={handlePublishSubmit}
              />
            )}
            {activeTab === 'requests' && (
              <RequestsTab
                requests={requests}
                requestsTotal={requestsTotal}
                isLoadingRequests={isLoadingRequests}
                onRefresh={() => void loadRequests()}
                onClear={() => void handleClearRequests()}
                onOpenDetail={openDetail}
              />
            )}
            {activeTab === 'messages' && (
              <MessagesTab
                messages={messages}
                messageTypeFilter={messageTypeFilter}
                setMessageTypeFilter={setMessageTypeFilter}
                messageTypeOptions={MESSAGE_TYPE_OPTIONS}
                messageSearch={messageSearch}
                setMessageSearch={setMessageSearch}
                isLoadingMessages={isLoadingMessages}
                onRefresh={() => void loadMessages()}
                onOpenDetail={openDetail}
              />
            )}
            {activeTab === 'admin' && (
              <AdminTab
                configMode={configMode}
                setConfigMode={setConfigMode}
                configRejectCode={configRejectCode}
                setConfigRejectCode={setConfigRejectCode}
                isSavingConfig={isSavingConfig}
                configResult={configResult}
                onSaveConfig={handleSaveConfig}
                isResetting={isResetting}
                resetResult={resetResult}
                onResetRuntime={() => void handleResetRuntime()}
              />
            )}
          </main>
        </div>
      </div>

      <DialogContent className="max-h-[85vh] overflow-hidden border-border bg-card p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="font-display text-sm font-bold uppercase tracking-widest">
            {detailState.title}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{detailState.description}</DialogDescription>
        </DialogHeader>
        <pre className="max-h-[calc(85vh-5.5rem)] overflow-auto bg-background px-6 py-5 font-mono text-xs leading-6 text-foreground/90">
          {prettyJson(detailState.data)}
        </pre>
      </DialogContent>

      <Toaster richColors position="bottom-right" />
    </Dialog>
  )
}

export function mountApp() {
  const container = document.getElementById('root')
  if (!container) throw new Error('Root element not found')
  const root = window.__icomMockUiRoot ?? createRoot(container)
  window.__icomMockUiRoot = root
  root.render(<App />)
}
